// src/pages/ServiceManagement/tabs/UpdatePathsTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingFlat as ArrowIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { servicesApi } from '../../../services/apiClient';

const UpdatePathsTab = ({ serviceId, onUpdate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [updatePaths, setUpdatePaths] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPath, setEditingPath] = useState(null);
  const [formData, setFormData] = useState({ actual_version: '', next_version: '' });
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pathsRes, versionsRes] = await Promise.all([
        servicesApi.getUpdatePaths(serviceId),
        servicesApi.getVersions(serviceId)
      ]);

      if (pathsRes.data.success) {
        setUpdatePaths(pathsRes.data.data || []);
      }
      if (versionsRes.data.success) {
        setVersions(versionsRes.data.data || []);
      }
    } catch (error) {
      console.error('Error loading update paths:', error);
      enqueueSnackbar('Failed to load update paths', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [serviceId]);

  const handleOpenDialog = (path = null) => {
    if (path) {
      setEditingPath(path);
      setFormData({ actual_version: path.actual_version, next_version: path.next_version });
    } else {
      setEditingPath(null);
      setFormData({ actual_version: '', next_version: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPath(null);
    setFormData({ actual_version: '', next_version: '' });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingPath) {
        await servicesApi.updateUpdatePath(editingPath.id, formData);
        enqueueSnackbar('Update path updated successfully', { variant: 'success' });
      } else {
        await servicesApi.createUpdatePath(serviceId, formData);
        enqueueSnackbar('Update path created successfully', { variant: 'success' });
      }
      loadData();
      handleCloseDialog();
      onUpdate();
    } catch (error) {
      console.error('Error saving update path:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save update path', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pathId) => {
    if (!window.confirm('Are you sure you want to delete this update path?')) return;

    try {
      await servicesApi.deleteUpdatePath(pathId);
      enqueueSnackbar('Update path deleted successfully', { variant: 'success' });
      loadData();
      onUpdate();
    } catch (error) {
      console.error('Error deleting update path:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to delete update path', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Update Paths</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={versions.length === 0}
        >
          Add Update Path
        </Button>
      </Box>

      {versions.length === 0 ? (
        <Alert severity="warning">
          You need to create versions first before defining update paths.
        </Alert>
      ) : updatePaths.length === 0 ? (
        <Alert severity="info">
          No update paths defined yet. Add an update path to specify version upgrade routes.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Current Version</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>
                  <ArrowIcon />
                </TableCell>
                <TableCell>Next Version</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {updatePaths.map((path) => (
                <TableRow key={path.id}>
                  <TableCell>
                    <Chip label={path.actual_version} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <ArrowIcon color="action" />
                  </TableCell>
                  <TableCell>
                    <Chip label={path.next_version} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    {path.createdAt ? new Date(path.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(path)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(path.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPath ? 'Edit Update Path' : 'Add Update Path'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              select
              label="Current Version"
              value={formData.actual_version}
              onChange={(e) => setFormData({ ...formData, actual_version: e.target.value })}
              helperText="The version to update from"
              required
            >
              {versions.map((version) => (
                <MenuItem key={version.id} value={version.version}>
                  {version.version}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="Next Version"
              value={formData.next_version}
              onChange={(e) => setFormData({ ...formData, next_version: e.target.value })}
              helperText="The version to update to"
              required
            >
              {versions.map((version) => (
                <MenuItem key={version.id} value={version.version}>
                  {version.version}
                </MenuItem>
              ))}
            </TextField>

            {formData.actual_version && formData.next_version && (
              <Alert severity="info">
                Update path: <strong>{formData.actual_version}</strong> → <strong>{formData.next_version}</strong>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.actual_version || !formData.next_version || saving}
            startIcon={saving && <CircularProgress size={20} />}
          >
            {editingPath ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdatePathsTab;
